import { safeText } from './serverText.js'

/* =========================================================================
 * THE PIPELINE VALUE, AND THE MATH BEHIND IT — pure. No React, no I/O.
 *
 * Brian: "Where is the pipeline value and the math behind it."
 *
 * So the product is NOT the deliverable; the FACTORS are. This turns an
 * /api/tam response into a render model where every factor is a row a person
 * can read and argue with, and where the four ways a money figure can lie are
 * each refused by construction:
 *
 *   1. A `null` rendered as `$0`.  `usd: null` means "there is no basis for a
 *      figure", and it arrives with a reason. Zero is a claim; null is an
 *      admission. The presenter never converts the second into the first.
 *   2. The CEILING rendered as the answer. 100% penetration is ~300x realised
 *      revenue. `headlineEligible: false` rides on it and is honoured on every
 *      surface, including team rows — a ceiling beside a rep's name reads as
 *      their number.
 *   3. $288 passed off as a measured average. It is Brian's FORWARD PRICE.
 *      History implies $223.71–$273.59, and that range is rendered beside it
 *      rather than hidden behind it.
 *   4. An aggregate factor rendered as a mean. `fieldSize` across many records
 *      is a DISTRIBUTION; 68.8% of dollars resting on the tier archetype is the
 *      largest single exposure in the number and is named as such.
 *
 * ⚠️ WHAT IS OBSERVED AND WHAT IS NOT. The DENIAL shape is verified against
 * production (2026-10-02, service identity): `{ ok:false, error:'rep_unmapped',
 * render:'error-not-empty', detail, provenance }`. The SUCCESS shape is not —
 * every /api/tam route answers rep_unmapped to a service identity, so no
 * successful payload could be captured. Everything below therefore reads
 * defensively: a factor that is absent renders as "not reported", never as a
 * zero and never as a crash, and no field is assumed to exist.
 * ========================================================================= */

const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`

/** A `{ usd, basis, reason }` figure, rendered honestly or refused. */
export function readFigure(fig, { label = 'Figure' } = {}) {
  if (!fig || typeof fig !== 'object') {
    return { label, value: null, text: 'not reported', reason: 'The response carried no figure here.', ok: false }
  }
  const { usd, basis, reason } = fig
  /* ⚠️ THE ONE THAT MATTERS. `usd == null` is not zero. */
  if (usd == null) {
    return {
      label, value: null, text: 'no basis for a figure',
      reason: reason || basis || 'No reason for the missing estimate was reported.',
      ok: false,
    }
  }
  if (typeof usd !== 'number' || !Number.isFinite(usd)) {
    return { label, value: null, text: 'not a number', reason: `Received ${JSON.stringify(usd)}.`, ok: false }
  }
  return { label, value: usd, text: money(usd), reason: reason || null, basis: basis || null, ok: true }
}

/**
 * The factor rows of `chain.factors`, in the order they multiply.
 * Each row is `{ key, label, value, qualifier, caveat }` — `qualifier` is the
 * thing that makes the factor inspectable (an n, an assumption, a bracket) and
 * is never dropped.
 */
export function readFactors(chain) {
  const f = chain?.factors
  if (!f || typeof f !== 'object') return []
  const rows = []

  /* ---- FIELD SIZE: a published bracket, or an archetype guess ---------- */
  if (f.fieldSize !== undefined) {
    const fs = f.fieldSize || {}
    const n = typeof fs.value === 'number' ? fs.value : (typeof fs === 'number' ? fs : null)
    const published = fs.isPublishedBracket === true
    const arch = fs.tierArchetype || null
    rows.push({
      key: 'fieldSize',
      label: 'Field size',
      value: n == null ? null : n.toLocaleString('en-US'),
      /* ⚠️ THE WEAKEST LINK, NAMED. A published bracket is a fact; an archetype
       * is a stand-in for one. Which it is changes how much the whole product is
       * worth believing, so it is on the row and not in a footnote. */
      qualifier: published
        ? 'published bracket: a stated figure'
        : 'Estimated from the competition tier, not a published bracket',
      caveat: fs.caveat || null,
      weak: !published,
      /* An aggregate is a distribution, never a mean. */
      distribution: arch && typeof arch.pctOfDollars === 'number'
        ? {
          pctOfDollars: arch.pctOfDollars,
          headline: arch.headline
            || `${arch.pctOfDollars}% of the dollars in this number rest on the tier archetype`,
        }
        : null,
    })
  }

  /* ---- UNITS PER BUYER: a mean, and the n it came from ----------------- */
  if (f.unitsPerBuyer !== undefined) {
    const u = f.unitsPerBuyer || {}
    const v = typeof u.value === 'number' ? u.value : (typeof u === 'number' ? u : null)
    const n = typeof u.n === 'number' ? u.n : null
    rows.push({
      key: 'unitsPerBuyer',
      label: 'Units per buyer',
      value: v == null ? null : String(v),
      /* A mean with no n is an opinion. */
      qualifier: n == null
        ? 'no sample size reported; this average is uncertain'
        : `mean of ${n.toLocaleString('en-US')} delivered order${n === 1 ? '' : 's'}`,
      caveat: u.caveat || u.confidence || null,
      weak: n == null,
      distribution: null,
    })
  }

  /* ---- PENETRATION: explicit, always, and never a headline ------------- */
  if (f.penetration !== undefined) {
    const p = f.penetration || {}
    const pct = typeof p.pct === 'number' ? p.pct : (typeof p.value === 'number' ? p.value * 100 : null)
    rows.push({
      key: 'penetration',
      label: 'Assumed buying rate',
      value: pct == null ? null : `${pct}%`,
      /* ⚠️ 100% IS A CEILING AND THE WORD HAS TO BE ON THE ROW. A reader who
       * sees "penetration 100%" and no assumption will take the product for a
       * forecast. */
      qualifier: p.assumption
        || (pct === 100 ? 'Assumes every organisation buys: a ceiling, not a forecast' : 'stated assumption'),
      caveat: pct === 100 ? 'At 100% this is the theoretical maximum, not an expectation.' : null,
      weak: pct === 100,
      distribution: null,
    })
  }

  /* ---- PRICE: Brian's forward price, with history beside it ------------ */
  if (f.pricePerUnitUsd !== undefined) {
    const pr = f.pricePerUnitUsd || {}
    const v = typeof pr.value === 'number' ? pr.value : (typeof pr === 'number' ? pr : null)
    const hist = pr.historyImplies || null
    rows.push({
      key: 'pricePerUnitUsd',
      label: 'Price per unit',
      value: v == null ? null : money(v),
      /* ⚠️ NOT A MEASURED AVERAGE, AND SAYING SO IS THE POINT. */
      qualifier: 'Planned price: set, not a measured average',
      caveat: formatPriceHistory(hist),
      weak: false,
      distribution: null,
    })
  }

  return rows
}

/** Known history shapes only. Never expose raw payloads or turn missing data into zero. */
export function formatPriceHistory(input) {
  const missing = 'Historical price comparison not reported.'
  const unreadable = 'Historical price comparison is unavailable in a readable format.'
  let h = input
  if (h == null || h === '') return missing
  if (typeof h === 'string') {
    try { h = JSON.parse(h) } catch {
      if (/^\s*[[{]/.test(h)) return unreadable
      return safeText(h, { fallback: unreadable }) || missing
    }
    if (typeof h === 'string') return safeText(h, { fallback: unreadable }) || missing
  }
  if (!h || typeof h !== 'object' || Array.isArray(h)) return unreadable
  const valid = n => typeof n === 'number' && Number.isFinite(n) && n >= 0
  const price = n => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
  const labels = [
    ['deliveredAndPaid', 'Delivered and paid orders'],
    ['inclDeliveredNotPaid', 'Delivered orders, including unpaid'],
    ['allDelivered', 'All delivered orders'],
  ]
  const known = labels.filter(([key]) => Object.hasOwn(h, key))
  if (known.length) return known.map(([key, label]) => `${label}: ${valid(h[key]) ? price(h[key]) : 'not reported'}`).join('; ') + '.'
  const lo = h.lowUsd ?? h.low ?? h.min
  const hi = h.highUsd ?? h.high ?? h.max
  if (valid(lo) && valid(hi) && lo <= hi) return `Historical price range: ${price(lo)} to ${price(hi)}.`
  if (valid(lo) && hi == null) return `Historical price lower bound: ${price(lo)}. Upper bound not reported.`
  if (valid(hi) && lo == null) return `Historical price upper bound: ${price(hi)}. Lower bound not reported.`
  return unreadable
}

/**
 * The whole view model for a /api/tam or /api/tam/record response.
 *
 * @returns {{
 *   kind: 'denied'|'ok',
 *   render: string|null,
 *   detail: string|null,
 *   headline: object|null,
 *   ceiling: object|null,
 *   formula: string|null,
 *   factors: Array,
 *   notPay: string|null,
 *   provenance: object|null,
 * }}
 */
export function tamView(res) {
  /* ⚠️ A DENIAL IS READ BEFORE ANYTHING IS DRAWN. The server says
   * `render: "error-not-empty"` precisely so a client cannot mistake a refusal
   * for an empty book — the same distinction lib/emptyReason.js draws for
   * counts, arriving here already decided by the side that knows. */
  const denied = res && (res.ok === false || res.render === 'error-not-empty')
  if (denied) {
    return {
      kind: 'denied',
      render: res.render || 'error-not-empty',
      error: res.error || null,
      detail: res.detail || 'This view was refused and gave no reason, which is itself a defect.',
      headline: null, ceiling: null, formula: null, factors: [],
      notPay: res.notPay || null,
      provenance: res.provenance || null,
    }
  }

  const chain = res?.chain || null
  const ceilingFig = res?.ceiling ? readFigure(res.ceiling, { label: 'At 100% penetration' }) : null

  /* ⚠️ `headlineEligible: false` IS HONOURED, NOT INTERPRETED. The ceiling may
   * never lead, on any surface, including a team row. The measured-conversion
   * figure leads; when it is unavailable the headline is ABSENT rather than
   * promoted from the ceiling. */
  const leadRaw = res?.lead || res?.observed || null
  const leadEligible = leadRaw ? res?.leadAvailable !== false && leadRaw.headlineEligible !== false : false
  const headline = leadEligible ? readFigure(leadRaw, { label: leadRaw.label || "At SR's measured conversion" }) : null

  return {
    kind: 'ok',
    render: res?.render || null,
    error: null,
    detail: res?.detail || null,
    headline,
    /* Always rendered SECOND and never eligible to lead. */
    ceiling: ceilingFig
      ? { ...ceilingFig, headlineEligible: res.ceiling?.headlineEligible === true }
      : null,
    formula: typeof chain?.formula === 'string' ? safeText(chain.formula)
      .replace(/\bfieldSize\b/g, 'Field size')
      .replace(/\bunitsPerBuyer\b/g, 'units per buyer')
      .replace(/\bpenetration\b/g, 'assumed buying rate')
      .replace(/\bpricePerUnitUsd\b/g, 'price per unit') : null,
    factors: readFactors(chain),
    product: chain?.product ? readFigure(chain.product, { label: 'Product of the factors' }) : null,
    notPay: res?.notPay || null,
    provenance: res?.provenance || null,
    confidence: res?.confidence || null,
    records: typeof res?.records === 'number' ? res.records : null,
    recordsReason: res?.recordsReason || null,
  }
}

/** How old is this, in words, from `provenance.records.readAt` / staleHours. */
export function tamAge(provenance) {
  const p = provenance?.records
  if (!p) return 'read time not reported'
  if (typeof p.staleHours === 'number') {
    const h = p.staleHours
    if (h < 1) return `read ${Math.max(1, Math.round(h * 60))}m ago`
    if (h < 48) return `read ${h.toFixed(1)}h ago`
    return `read ${Math.round(h / 24)}d ago`
  }
  return p.readAt ? `read ${p.readAt}` : 'read time not reported'
}
